import React, { useState, useEffect, useCallback } from 'react';
import { getMovimientosPaginados } from '../services/movimientoService';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileText, User, Monitor, Hash, Calendar, DollarSign, Tag, Info } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

const MovimientosList = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortField, setSortField] = useState('fecha');
    const [sortDir, setSortDir] = useState('desc');
    const [goToPage, setGoToPage] = useState('1');

    const cargarMovimientos = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMovimientosPaginados(page, size, search, sortField, sortDir);
            setMovimientos(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setGoToPage((page + 1).toString());
        } catch (error) {
            toast.error('Error al cargar movimientos');
        } finally {
            setLoading(false);
        }
    }, [page, size, search, sortField, sortDir]);

    useEffect(() => {
        cargarMovimientos();
    }, [cargarMovimientos]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('es-PY');
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const p = parseInt(goToPage);
            if (!isNaN(p)) {
                setPage(Math.max(0, Math.min(p - 1, totalPages - 1)));
            }
        }
    };

    const handlePageChange = (newPage) => {
        setPage(Math.max(0, Math.min(newPage - 1, totalPages - 1)));
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <Toaster position="top-right" />
            
            <div className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                            Auditoría de Movimientos
                        </h1>
                        <p className="text-slate-500 font-medium text-sm">
                            Consulta histórica de transacciones y operaciones del sistema
                        </p>
                    </div>
                </div>
            </div>

            {/* Filters Section */}
            <div className="mb-6 flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-4 border border-slate-200 rounded-xl shadow-sm">
                <div className="relative w-full md:w-96">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar por concepto o referencia..."
                        className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                        value={search}
                        onChange={handleSearch}
                    />
                </div>
                <button
                    onClick={cargarMovimientos}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all text-xs font-bold shadow-sm"
                >
                    <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
                    {loading ? 'Cargando...' : 'Actualizar'}
                </button>
            </div>

            <div className="bg-white border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-200 bg-white">
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-16">ID</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-32">Fecha / Hora</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider">Socio / Cuenta</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24">Tipo</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider text-right w-32">Monto</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider">Concepto</th>
                                <th className="px-4 py-3 text-[11px] font-black text-slate-400 uppercase tracking-wider w-24 text-center">Usuario</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr>
                                <td colSpan="11" className="px-6 py-10 text-center">
                                    <div className="flex justify-center">
                                        <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
                                    </div>
                                    <p className="mt-2 text-slate-500">Recuperando transacciones...</p>
                                </td>
                            </tr>
                        ) : movimientos.length === 0 ? (
                            <tr>
                                <td colSpan="11" className="px-6 py-10 text-center text-slate-500">
                                    No se encontraron movimientos registrados
                                </td>
                            </tr>
                        ) : (
                            movimientos.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-4 py-3 text-xs font-bold text-slate-400">
                                        #{item.id}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-medium text-slate-500">
                                        {formatDate(item.fecha)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                                                {item.cuenta?.socio ? `${item.cuenta.socio.nombre} ${item.cuenta.socio.apellido}` : 'OP. INTERNA'}
                                            </span>
                                            <span className="text-[10px] font-mono text-slate-400">
                                                {item.cuenta ? `${item.cuenta.tipoCuenta?.codigo} ${item.cuenta.numeroCuenta}` : 'S/C'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                            item.tipoMovimiento?.codigo === 'CR' 
                                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
                                            : 'bg-rose-50 text-rose-600 border border-rose-100'
                                        }`}>
                                            {item.tipoMovimiento?.nombre || item.tipoMovimiento?.codigo || '-'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm font-black text-slate-900">
                                        {formatCurrency(item.monto)}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate" title={item.concepto}>
                                        {item.concepto}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center gap-1.5">
                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500 border border-slate-200">
                                                {(item.usuario?.nombreUsuario || 'S')[0].toUpperCase()}
                                            </div>
                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                                                {item.usuario?.nombreUsuario || 'SISTEMA'}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            )))}
                        </tbody>
                    </table>
                </div>

                {/* Paginación estilo Premium */}
                {!loading && totalElements > 0 && (
                    <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ir a</span>
                                <input 
                                    type="text" 
                                    className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                    value={goToPage}
                                    onChange={(e) => setGoToPage(e.target.value)}
                                    onKeyDown={handleGoToPage}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">Ver</span>
                                <select 
                                    className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={size}
                                    onChange={(e) => {
                                        setSize(Number(e.target.value));
                                        setPage(0);
                                    }}
                                >
                                    {[10, 20, 50, 100].map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center gap-1">
                            <button 
                                onClick={() => setPage(0)}
                                disabled={page === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${page === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsLeft className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${page === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            <div className="flex items-center mx-2 gap-1">
                                {[...Array(totalPages)].map((_, i) => {
                                    const p = i + 1;
                                    if (totalPages > 5) {
                                        if (p !== 1 && p !== totalPages && Math.abs(p - (page + 1)) > 1) {
                                            if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                            return null;
                                        }
                                    }
                                    return (
                                        <button
                                            key={p}
                                            onClick={() => handlePageChange(p)}
                                            className={`w-8 h-8 rounded text-xs font-bold transition-all ${page + 1 === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                        >
                                            {p}
                                        </button>
                                    );
                                })}
                            </div>

                            <button 
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page === totalPages - 1 || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${page === totalPages - 1 || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={() => setPage(totalPages - 1)}
                                disabled={page === totalPages - 1 || totalPages === 0}
                                className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${page === totalPages - 1 || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                            >
                                <ChevronsRight className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                            [{totalElements > 0 ? (page * size) + 1 : 0} a {Math.min((page + 1) * size, totalElements)} de {totalElements}]
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MovimientosList;
